import { cx } from '../primitives/cx.js';
import { passthrough } from '../primitives/passthrough.js';
import { Icon } from '../primitives/Icon.tsx';
import { Num } from '../primitives/Num.tsx';
import { SectionLabel } from '../primitives/Surface.tsx';

/**
 * MRRWaterfall - where recurring revenue went, and what is behind each step.
 *
 * Four things it will not do.
 *
 * **It does no arithmetic.** Every figure arrives already formatted, and the
 * caller also passes the reconciliation the server computed. A chart that
 * recomputed its own opening-plus-movements-equals-closing would be a second
 * answer to a question the ledger already answered, and the second answer is
 * the one people would read off the screen.
 *
 * **It refuses to draw a waterfall that does not balance.** If the movements
 * do not add up to the closing figure, it says so, in words, above the bars,
 * and marks itself `data-reconciled="false"`. Drawing the bars anyway would
 * produce a picture indistinguishable from a correct one - and somebody would
 * carry it into a meeting.
 *
 * **It never says a direction with colour alone.** Each step carries its own
 * label and an arrow glyph as well as its colour, because a churn bar that is
 * only red is a bar that says nothing in a printed board pack and nothing to a
 * reader who cannot see red.
 *
 * **Every step is a link.** A figure nobody can interrogate does not get
 * trusted, and the first question anyone asks of a churn number is which
 * customers.
 */

export type WaterfallStepKind = 'opening' | 'new' | 'expansion' | 'reactivation' | 'contraction' | 'churn' | 'closing';

export interface WaterfallStep {
  kind: WaterfallStepKind;
  /** The caller's own word for it. This component ships no copy. */
  label: string;
  /** Already through the formatter. Never a number. */
  value: string;
  /**
   * The magnitude used to size the bar, as a plain number.
   *
   * Separate from `value` because a bar's height is geometry, not money: it
   * must never be parsed back out of a formatted string, which is where a
   * currency symbol containing full stops produced NaN once already.
   */
  magnitude: number;
  /** How many subscriptions are behind this step. Already formatted. */
  count?: string | undefined;
  href?: string | undefined;
}

export interface MRRWaterfallLabels {
  /** Names the chart for a screen reader. */
  chart: string;
  /** Announced on a step that adds, and one that takes away. */
  increase: string;
  decrease: string;
  /** Shown instead of bars when the movements do not reach the closing figure. */
  doesNotBalance: (difference: string) => string;
  /** The invitation on each step. */
  drillThrough: (step: string) => string;
  /** Shown when nothing moved in the period. */
  nothingMoved: string;
}

export interface MRRWaterfallProps {
  steps: WaterfallStep[];
  labels: MRRWaterfallLabels;
  /**
   * Straight from the server's own reconciliation.
   *
   * Passed rather than derived: this component is not entitled to an opinion
   * about whether the ledger adds up, only to refuse to draw it when the
   * ledger says it does not.
   */
  reconciliation: { balances: boolean; difference: string };
}

/** Which way each kind of step moves the total. */
const DIRECTION: Record<WaterfallStepKind, 'up' | 'down' | 'total'> = {
  opening: 'total',
  new: 'up',
  expansion: 'up',
  reactivation: 'up',
  contraction: 'down',
  churn: 'down',
  closing: 'total',
};

/**
 * Four movements, four colours - and never colour alone.
 *
 * Expansion and reactivation are both growth but are not the same event, so
 * they do not share a colour: a month that grew because lapsed customers came
 * back is a different business from one that grew because existing customers
 * bought more, and a chart that paints them identically hides the difference
 * that matters.
 */
const TONE: Record<WaterfallStepKind, string> = {
  opening: 'bg-ink-400',
  new: 'bg-mark-success',
  expansion: 'bg-mark-info',
  reactivation: 'bg-bronze',
  contraction: 'bg-mark-warning',
  churn: 'bg-mark-danger',
  closing: 'bg-ink-400',
};

export function MRRWaterfall({
  steps,
  labels,
  reconciliation,
  ...rest
}: MRRWaterfallProps & Record<string, unknown>): JSX.Element {
  // The tallest bar sets the scale. Zero-safe: an empty period would otherwise
  // divide by nothing and render every bar full height, which reads as a busy
  // quarter when the truth is that nothing happened.
  const tallest = steps.reduce((highest, step) => Math.max(highest, Math.abs(step.magnitude)), 0);
  const moved = steps.some((step) => DIRECTION[step.kind] !== 'total' && step.magnitude !== 0);

  return (
    <section
      {...passthrough(rest)}
      data-component="MRRWaterfall"
      data-reconciled={reconciliation.balances ? 'true' : 'false'}
      aria-label={labels.chart}
      className="flex w-full flex-col gap-2"
    >
      {!reconciliation.balances ? (
        /*
         * The refusal, and it is deliberately not a small print footnote.
         *
         * A waterfall whose parts do not reach its total is wrong in a way
         * that looks exactly like being right, so the bars do not get drawn at
         * all - the reader is told the figures disagree and by how much,
         * rather than being handed a picture to misread.
         */
        <p
          role="alert"
          data-waterfall-unbalanced
          className="flex items-start gap-2 rounded-card bg-danger-tint px-3 py-2 text-dense text-danger-text"
        >
          <Icon name="Warning" size="inline" decorative />
          {labels.doesNotBalance(reconciliation.difference)}
        </p>
      ) : !moved ? (
        <p data-waterfall-flat className="rounded-card bg-sunken px-3 py-2 text-dense text-ink-meta">
          {labels.nothingMoved}
        </p>
      ) : null}

      {reconciliation.balances ? (
        <ol className="flex w-full items-end gap-2 overflow-x-auto pb-1" data-scroll-container>
          {steps.map((step) => {
            const direction = DIRECTION[step.kind];
            const height = tallest === 0 ? 0 : Math.round((Math.abs(step.magnitude) / tallest) * 100);
            const bar = (
              <>
                {/* The bar. Geometry only: every word is below it. */}
                <span
                  data-waterfall-bar={step.kind}
                  data-height={String(height)}
                  aria-hidden
                  className={cx('w-full rounded-control', TONE[step.kind])}
                  // The one inline style in the component: a bar's height is a
                  // continuous value, and a utility scale cannot express it.
                  style={{ height: `${String(Math.max(height, 2))}%` }}
                />
                <span className="flex flex-col items-center gap-0.5 pt-1">
                  {/* The direction, as a glyph and a word - never the colour
                      on its own. */}
                  {direction === 'total' ? null : (
                    <span className="inline-flex items-center gap-1 text-meta text-ink-meta">
                      <Icon
                        name={direction === 'up' ? 'ArrowUp' : 'ArrowDown'}
                        size="inline"
                        decorative
                      />
                      <span className="sr-only">
                        {direction === 'up' ? labels.increase : labels.decrease}
                      </span>
                    </span>
                  )}
                  <SectionLabel>{step.label}</SectionLabel>
                  <Num
                    data-waterfall-value={step.kind}
                    align="start"
                    className={cx(
                      'text-body',
                      direction === 'total' ? 'text-ink' : 'text-ink-600',
                    )}
                  >
                    {step.value}
                  </Num>
                  {step.count === undefined ? null : (
                    /* How many customers are behind the figure. An MRR that
                       moved because one account doubled is a different fact
                       from one that moved because twenty arrived, and the
                       amount alone cannot tell them apart. */
                    <Num data-waterfall-count={step.kind} align="start" className="text-meta text-ink-meta">
                      {step.count}
                    </Num>
                  )}
                </span>
              </>
            );

            return (
              <li
                key={step.kind}
                data-waterfall-step={step.kind}
                className="flex h-plot min-w-chart-column flex-1 flex-col justify-end"
              >
                {step.href === undefined ? (
                  <span className="flex h-full flex-col justify-end">{bar}</span>
                ) : (
                  <a
                    href={step.href}
                    data-waterfall-link={step.kind}
                    aria-label={labels.drillThrough(step.label)}
                    className="flex h-full flex-col justify-end rounded-card focus-visible:outline-none hover:bg-sunken"
                  >
                    {bar}
                  </a>
                )}
              </li>
            );
          })}
        </ol>
      ) : null}
    </section>
  );
}
